import * as React from 'react'

import { Button, Input } from '@thg-commerce/gravity-elements'
import { styled } from '@thg-commerce/gravity-patterns/theme'
import { margin, mq, padding, spacing, Text } from '@thg-commerce/gravity-theme'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

export interface StoreSearchProps {
  i18nText: {
    title: string
    subtitle: string
    inputLabel: string
    buttonLabel: string
    infoLabel?: string
    infoLinkLabel?: string
  }
  alignment: HorizontalAlignment
  searchInputOverride?: string
  setSearchInputOverride?: (value: string) => void
  selectedStoreLocation?: {
    latitude: number
    longitude: number
  }
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus: (event: React.FocusEvent) => void
  clearInputValueHandler: (event: React.SyntheticEvent) => void
  closeDropdown: () => void
  autocompletePredictions?: JSX.Element | null
  showInfoLabel?: boolean
  searchStoreHandler: (value: string) => void
  storeLocatorCountry?: string
}

const StoreSearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: ${(props) =>
    props.theme.patterns.storeSearch.backgroundColor};

  ${(props) => padding(props.theme.patterns.storeSearch.padding)};
`

const StoreSearchTitle = styled.h1<{ alignment: HorizontalAlignment }>`
  ${(props) =>
    Text(
      props.theme.patterns.storeSearch.title.font.entry,
      props.theme.patterns.storeSearch.title.font.style,
    )};

  width: 100%;
  max-width: 310px;
  text-align: center;
  color: ${(props) => props.theme.patterns.storeSearch.title.font.textColor};
  text-decoration: ${(props) =>
    props.theme.patterns.storeSearch.title.font.textDecoration};

  ${(props) => margin(props.theme.patterns.storeSearch.title.margin)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    max-width: 416px;
    text-align: ${(props) => props.alignment};
    margin-right: auto;
    margin-left: ${(props) =>
      props.alignment === HorizontalAlignment.CENTER ? 'auto' : '0'};
  }
`

const StoreSearchSubtitle = styled.p<{ alignment: HorizontalAlignment }>`
  ${(props) =>
    Text(
      props.theme.patterns.storeSearch.subtitle.font.entry,
      props.theme.patterns.storeSearch.subtitle.font.style,
    )};

  width: 100%;
  text-align: center;
  color: ${(props) => props.theme.patterns.storeSearch.subtitle.font.textColor};
  text-transform: ${(props) =>
    props.theme.patterns.storeSearch.subtitle.font.transform};
  text-decoration: ${(props) =>
    props.theme.patterns.storeSearch.subtitle.font.textDecoration};

  ${(props) => margin(props.theme.patterns.storeSearch.subtitle.margin)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    text-align: ${(props) => props.alignment};
  }
`

const SearchForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
`

const InputWrapper = styled.div<{ alignment: HorizontalAlignment }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  width: 100%;
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    width: ${(props) =>
      props.alignment === HorizontalAlignment.CENTER
        ? props.theme.patterns.storeSearch.searchInput.width
        : `100%`};
  }

  ${(props) => margin(props.theme.patterns.storeSearch.searchInput.margin)};

  > div {
    width: 100%;
    ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
      width: ${(props) =>
        props.alignment === HorizontalAlignment.CENTER
          ? props.theme.patterns.storeSearch.searchInput.width
          : `100%`};
    }
  }
`

const SearchButton = styled(Button)`
  height: 48px;
  width: 120px;
`

const StyledInput = styled(Input)`
  border-radius: ${(props) =>
    props.theme.patterns.storeSearch.searchInput.borderRadius};
`

const InfoLabel = styled.span`
  width: 100%;
  margin-top: ${spacing(2)};
  ${Text('bodyText', 'default')};
  text-align: left;
`

const StyledLinkText = styled(Button)`
  ${Text('bodyText', 'alternate')};
  text-decoration: underline;
  height: 48px;
`

export const StoreSearch = (props: StoreSearchProps) => {
  const { closeDropdown, ...otherProps } = props
  const inputRef = React.useRef(null)
  const autocompleteRef = React.useRef(null)
  const [inputValue, setInputValue] = React.useState<string>(
    otherProps.searchInputOverride || '',
  )

  React.useEffect(() => {
    setInputValue(otherProps.searchInputOverride || '')
  }, [otherProps.searchInputOverride])

  const handleClickOutside = React.useCallback(
    (event) => {
      if (
        inputRef.current &&
        inputRef.current !== event.target &&
        autocompleteRef.current &&
        autocompleteRef.current !== event.target
      ) {
        closeDropdown()
      }
    },
    [closeDropdown],
  )

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [handleClickOutside])

  const handleSearchSubmit = () => {
    if (otherProps.searchStoreHandler) {
      otherProps.searchStoreHandler(inputValue)
    }
  }

  const storeCountryUrl = `/stores/${otherProps.storeLocatorCountry}`

  return (
    <StoreSearchWrapper>
      <StoreSearchTitle alignment={otherProps.alignment}>
        {otherProps.i18nText.title}
      </StoreSearchTitle>
      <StoreSearchSubtitle alignment={otherProps.alignment}>
        {otherProps.i18nText.subtitle}
      </StoreSearchSubtitle>
      <SearchForm
        onSubmit={(event) => {
          event.preventDefault()
          handleSearchSubmit()
        }}
      >
        <InputWrapper alignment={otherProps.alignment}>
          <StyledInput
            valueOverride={otherProps.searchInputOverride || inputValue || ''}
            label={otherProps.i18nText.inputLabel}
            required={false}
            type="text"
            onChange={(event) => {
              setInputValue(event.target.value)
              otherProps.onChange(event)
            }}
            onFocus={otherProps.onFocus}
            clearInputValueHandler={otherProps.clearInputValueHandler}
            bindref={inputRef}
          />
          <div ref={autocompleteRef}>{otherProps.autocompletePredictions}</div>
        </InputWrapper>
        <SearchButton type="submit" emphasis="high">
          {otherProps.i18nText.buttonLabel}
        </SearchButton>
      </SearchForm>
      {otherProps.showInfoLabel &&
        otherProps.i18nText.infoLabel &&
        otherProps.i18nText.infoLinkLabel && (
          <InfoLabel>
            {otherProps.i18nText.infoLabel}
            {` `}
            <StyledLinkText
              emphasis="low"
              renderedAs="a"
              href={storeCountryUrl}
            >
              {otherProps.i18nText.infoLinkLabel}
            </StyledLinkText>
          </InfoLabel>
        )}
    </StoreSearchWrapper>
  )
}
